import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Heading,
  Text,
  Divider,
  CardNotification,
  Link as LinkAvocado,
} from "@foris/avocado-suite";
import { LinkSelector } from "@modules/shared/selectors/Selectors";
import { ContextEDH } from "@context/ContextEDH";
import { Link } from "@models/ISchema";
import { IGroup, ILeague } from "@modules/vacancies/contextVacancies/ContextVacancies";
import styles from "./groupMigrationDrawer.module.scss";

interface GroupMigrationDrawerContentProps {
  linkDetails?: ILeague;
  groupDetails?: IGroup;
  hasSizeError?: boolean;
  hasClassroomCapacityError?: boolean;
  isLoading?: boolean;
  shouldAvoidErrors?: boolean;
  selectedLink?: Link | null;
  setSelectedLink?: (value: Link | null) => void;
  setShouldAvoidErrors?: (value: boolean) => void;
  onCancel?: () => void;
}

const GroupMigrationDrawerContent = ({
  linkDetails,
  groupDetails,
  hasSizeError,
  hasClassroomCapacityError,
  isLoading,
  shouldAvoidErrors,
  selectedLink,
  setSelectedLink,
  setShouldAvoidErrors,
}: GroupMigrationDrawerContentProps) => {
  const { state } = useContext(ContextEDH);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    setSelectedLink(null);
  }, [language]);

  return (
    <section className={styles.groupMigrationDrawerContent}>
      <div className={styles.informationBox}>
        <Heading type="h4" className={styles.informationBox_title}>
          {t("bundle.migrated-group-link.forms.information-box.title")}
        </Heading>

        <div className={styles.informationBox_content}>
          <Text type="sm" className={styles.text}>
            {t("bundle.migrated-group-link.forms.information-box.group")}:
          </Text>
          <Text type="sm" weight="medium" className={styles.bold}>
            {groupDetails?.label || groupDetails?.clientCode || "-"}
          </Text>
          <Divider vertical className={styles.divider} />
          <Text type="sm" className={styles.text}>
            {t("bundle.migrated-group-link.forms.information-box.vacancies")}:
          </Text>
          <Text type="sm" weight="medium" className={styles.bold}>
            {groupDetails?.vacancies ?? "-"}
          </Text>
          <Divider vertical className={styles.divider} />
          <Text type="sm" className={styles.text}>
            {t("bundle.migrated-group-link.forms.information-box.enrollments")}:
          </Text>
          <Text type="sm" weight="medium" className={styles.bold}>
            {groupDetails?.enrollment ?? "-"}
          </Text>
          <Divider vertical className={styles.divider} />
          <Text type="sm" className={styles.text}>
            {t("bundle.migrated-group-link.forms.information-box.waiting-list")}:
          </Text>
          <Text type="sm" weight="medium" className={styles.bold}>
            {groupDetails?.waitList ?? "-"}
          </Text>
        </div>

        <Text type="sm" className={styles.informationBox_subtitle}>
          {t("bundle.migrated-group-link.forms.information-box.subtitle.title")}:
        </Text>

        <div className={styles.informationBox_content}>
          <Text type="sm" className={styles.text}>
            {t("bundle.migrated-group-link.forms.information-box.subtitle.course")}:
          </Text>
          <Text type="sm" weight="medium" className={styles.bold}>
            {linkDetails?.course?.name || "-"}
          </Text>
          <Divider vertical className={styles.divider} />
          <Text type="sm" className={styles.text}>
            {t("bundle.migrated-group-link.forms.information-box.subtitle.campus")}:
          </Text>
          <Text type="sm" weight="medium" className={styles.bold}>
            {linkDetails?.course?.curriculum?.program?.campus?.code ?? "-"}
          </Text>
          <Divider vertical className={styles.divider} />
          <Text type="sm" className={styles.text}>
            {t("bundle.migrated-group-link.forms.information-box.subtitle.period")}:
          </Text>
          <Text type="sm" weight="medium" className={styles.bold}>
            {linkDetails?.bundle?.term?.code || "-"}
          </Text>
        </div>
      </div>

      <LinkSelector
        key={language}
        className={styles.linkSelector}
        label={t("bundle.migrated-group-link.forms.select-link-destiny.label")}
        placeholder={t("bundle.migrated-group-link.forms.select-link-destiny.placeholder")}
        onSelectLink={setSelectedLink}
        isDisabled={isLoading}
        linksToAvoid={[linkDetails?.linkId]}
        getUnrestrictedLinks={state?.base?.groupsInLinkRestriction?.enabled ? true : undefined}
      />

      {selectedLink && (
        <div className={styles.customInformationBox}>
          <Heading type="h4" className={styles.informationBox_title}>
            {t("bundle.migrated-group-link.forms.information-box.key-destiny.title")}:
          </Heading>

          <div className={styles.informationBox_content}>
            <Text type="sm" className={styles.text}>
              {t("bundle.migrated-group-link.forms.information-box.key-destiny.course")}:
            </Text>
            <Text type="sm" weight="medium" className={styles.bold}>
              {selectedLink?.course?.name || "-"}
            </Text>
            <Divider vertical className={styles.divider} />
            <Text type="sm" className={styles.text}>
              {t("bundle.migrated-group-link.forms.information-box.key-destiny.campus")}:
            </Text>
            <Text type="sm" weight="medium" className={styles.bold}>
              {selectedLink?.course?.curriculum?.program?.campus?.code ?? "-"}
            </Text>
            <Divider vertical className={styles.divider} />
            <Text type="sm" className={styles.text}>
              {t("bundle.migrated-group-link.forms.information-box.key-destiny.period")}:
            </Text>
            <Text type="sm" weight="medium" className={styles.bold}>
              {selectedLink?.bundle?.term?.code || "-"}
            </Text>
          </div>
        </div>
      )}

      {(hasClassroomCapacityError || hasSizeError) && (
        <CardNotification
          outlined
          className={styles.notificationWarn}
          title={t("bundle.migrated-group-link.forms.notification-warn-exceeded-capacity.title")}
          state="warning"
          extraContent={
            !shouldAvoidErrors && (
              <LinkAvocado
                className={styles.notificationWarn_link}
                onClick={() => {
                  setShouldAvoidErrors(true);
                }}
              >
                {t("bundle.migrated-group-link.forms.notification-warn-exceeded-capacity.btn-link")}
              </LinkAvocado>
            )
          }
        >
          <>
            <Text type="sm" className={styles.notificationWarn_text}>
              {t("bundle.migrated-group-link.forms.notification-warn-exceeded-capacity.list.title")}
            </Text>
            {(hasClassroomCapacityError || hasSizeError) && (
              <ul className={styles.notificationWarn_list}>
                {hasClassroomCapacityError && (
                  <li>
                    <Text type="sm">
                      {t(
                        "bundle.migrated-group-link.forms.notification-warn-exceeded-capacity.list.item-1",
                      )}
                    </Text>
                  </li>
                )}

                {hasSizeError && (
                  <li>
                    <Text type="sm">
                      {t(
                        "bundle.migrated-group-link.forms.notification-warn-exceeded-capacity.list.item-2",
                      )}
                    </Text>
                  </li>
                )}
              </ul>
            )}
          </>
        </CardNotification>
      )}
    </section>
  );
};

export default GroupMigrationDrawerContent;
